import React, { useState, useEffect, useCallback } from 'react';
import { generateSeatId, getRowLabel } from '../utils/seatNaming';
import { api } from '../services/api';
import { toast, confirm } from '../utils/swal';
import AppHeader from './AppHeader';
import './SeatBlockManager.css';

const SeatBlockManager = ({
  event, venue, onBack,
  onGoToProfile, onGoToEventManagement, onGoToAdminPanel, onGoToVenueManage, onGoToQrScanner,
}) => {
  const [blockedSeats, setBlockedSeats]   = useState({});
  const [reservedSeats, setReservedSeats] = useState(new Set());
  const [loading, setLoading]             = useState(true);
  const [saving, setSaving]               = useState(false);
  const [hasChanges, setHasChanges]       = useState(false);
  const [selectedType, setSelectedType]   = useState('protocol');

  useEffect(() => {
    Promise.all([
      api.getBlockedSeats(event.id),
      api.getReservedSeats(event.id),
    ]).then(([blocked, reserved]) => {
      const map = {};
      (blocked || []).forEach(s => { map[s.seatId] = s.type; });
      setBlockedSeats(map);
      setReservedSeats(new Set(Array.isArray(reserved) ? reserved : []));
    }).catch(() => toast.error('Veriler yüklenemedi!'))
      .finally(() => setLoading(false));
  }, [event.id]);

  const handleSeatClick = useCallback((seatId) => {
    if (reservedSeats.has(seatId)) return; // rezerve koltuk dokunulamaz
    setBlockedSeats(prev => {
      const next = { ...prev };
      if (next[seatId] === selectedType) {
        delete next[seatId];
      } else {
        next[seatId] = selectedType;
      }
      return next;
    });
    setHasChanges(true);
  }, [selectedType, reservedSeats]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = Object.entries(blockedSeats).map(([seatId, type]) => ({ seatId, type }));
      await api.updateBlockedSeats(event.id, payload);
      toast.success('Koltuk düzeni kaydedildi!');
      setHasChanges(false);
    } catch {
      toast.error('Kaydedilemedi!');
    } finally {
      setSaving(false);
    }
  };

  const handleClearAll = async () => {
    const res = await confirm.action('Tüm blokları kaldır?', 'Tüm protokol ve davetli ayrımları silinecek.', 'Evet, temizle');
    if (!res.isConfirmed) return;
    setBlockedSeats({});
    setHasChanges(true);
  };

  const getSeatClass = (seatId) => {
    if (reservedSeats.has(seatId))           return 'seat seat-reserved';
    if (blockedSeats[seatId] === 'protocol') return 'seat seat-protocol';
    if (blockedSeats[seatId] === 'guest')    return 'seat seat-guest';
    return 'seat seat-available';
  };

  const protocolCount = Object.values(blockedSeats).filter(t => t === 'protocol').length;
  const guestCount    = Object.values(blockedSeats).filter(t => t === 'guest').length;

  // ── 2D Layout — TicketingSeatMap ile aynı algoritma ──
  const SEAT_W = 30, SEAT_H = 30, ROW_LABEL_W = 36, SECTION_PADDING = 12, SECTION_HEADER_H = 52, GAP = 16;
  const ROW_THRESHOLD = 50;

  const getSectionRenderSize = (s) => {
    const rowCount  = s.endRow - s.startRow + 1;
    const maxSeats  = s.seatsPerRow;
    const w = ROW_LABEL_W + maxSeats * (SEAT_W + 4) + SECTION_PADDING * 2;
    const h = SECTION_HEADER_H + rowCount * (SEAT_H + 4) + SECTION_PADDING * 2;
    return { w, h };
  };

  const hasPositions = venue.sections.some(s => s.position?.x !== undefined);

  const computeLayout = () => {
    const sorted = [...venue.sections].sort((a, b) => (a.position?.y || 0) - (b.position?.y || 0));
    const rows = [];
    sorted.forEach(s => {
      const sy = s.position?.y || 0;
      const lastRow = rows[rows.length - 1];
      if (lastRow && Math.abs((lastRow[0].position?.y || 0) - sy) < ROW_THRESHOLD) {
        lastRow.push(s);
      } else {
        rows.push([s]);
      }
    });
    rows.forEach(row => row.sort((a, b) => (a.position?.x || 0) - (b.position?.x || 0)));

    const positions = new Map();
    let currentTop = 0;
    rows.forEach(row => {
      let currentLeft = 0;
      const rowHeight = Math.max(...row.map(s => getSectionRenderSize(s).h));
      row.forEach(s => {
        const { w } = getSectionRenderSize(s);
        positions.set(s.id, { left: currentLeft, top: currentTop });
        currentLeft += w + GAP;
      });
      currentTop += rowHeight + GAP;
    });
    return positions;
  };

  const renderSection = (section, style = {}) => {
    const totalRows = section.endRow - section.startRow + 1;
    return (
      <div key={section.id} className="sbm-section" style={style}>
        <div className="sbm-section-header">{section.name}</div>
        <div className="sbm-seats-grid">
          {Array.from({ length: totalRows }, (_, rowIndex) => {
            const seatsInRow = section.rowSeats?.[rowIndex] || section.seatsPerRow;
            return (
              <div key={rowIndex} className="sbm-seat-row">
                <span className="sbm-row-label">{getRowLabel(section, rowIndex)}</span>
                {Array.from({ length: seatsInRow }, (_, seatIndex) => {
                  const seatId = generateSeatId(section, rowIndex, seatIndex);
                  const displayNum = section.reverseSeats
                    ? (section.seatStartNumber ?? 1) + seatsInRow - 1 - seatIndex
                    : (section.seatStartNumber ?? 1) + seatIndex;
                  const isReserved = reservedSeats.has(seatId);
                  return (
                    <button
                      key={seatIndex}
                      className={getSeatClass(seatId)}
                      onClick={() => handleSeatClick(seatId)}
                      disabled={isReserved}
                      title={
                        isReserved                           ? `${seatId} — Rezerve edilmiş` :
                        blockedSeats[seatId] === 'protocol'  ? `${seatId} — Protokol` :
                        blockedSeats[seatId] === 'guest'     ? `${seatId} — Davetli` :
                        `${seatId} — Boş`
                      }
                    >
                      {displayNum}
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="sbm-root">
        <AppHeader title={`🗂️ Koltuk Düzeni — ${event.name}`} onBack={onBack} backLabel="← Etkinlik Listesi"
          onGoToProfile={onGoToProfile} onGoToEventManagement={onGoToEventManagement}
          onGoToAdminPanel={onGoToAdminPanel} onGoToVenueManage={onGoToVenueManage} onGoToQrScanner={onGoToQrScanner} />
        <div className="sbm-loading">Yükleniyor...</div>
      </div>
    );
  }

  // 2D layout için pozisyonları hesapla
  const sectionPositions = hasPositions ? computeLayout() : null;
  const canvasW = sectionPositions
    ? Math.max(...venue.sections.map(s => (sectionPositions.get(s.id)?.left || 0) + getSectionRenderSize(s).w)) + 40
    : null;
  const canvasH = sectionPositions
    ? Math.max(...venue.sections.map(s => (sectionPositions.get(s.id)?.top || 0) + getSectionRenderSize(s).h)) + 40
    : null;

  return (
    <div className="sbm-root">
      <AppHeader
        title={`🗂️ Koltuk Düzeni — ${event.name}`}
        onBack={onBack}
        backLabel="← Etkinlik Listesi"
        onGoToProfile={onGoToProfile}
        onGoToEventManagement={onGoToEventManagement}
        onGoToAdminPanel={onGoToAdminPanel}
        onGoToVenueManage={onGoToVenueManage}
        onGoToQrScanner={onGoToQrScanner}
      />

      {/* Araç çubuğu */}
      <div className="sbm-toolbar">
        <div className="sbm-type-selector">
          <span className="sbm-toolbar-label">İşaretleme tipi:</span>
          <button className={`sbm-type-btn protocol ${selectedType === 'protocol' ? 'active' : ''}`} onClick={() => setSelectedType('protocol')}>
            🔵 Protokol
          </button>
          <button className={`sbm-type-btn guest ${selectedType === 'guest' ? 'active' : ''}`} onClick={() => setSelectedType('guest')}>
            🟣 Davetli
          </button>
        </div>
        <div className="sbm-counts">
          <span className="sbm-count protocol">Protokol: <strong>{protocolCount}</strong></span>
          <span className="sbm-count guest">Davetli: <strong>{guestCount}</strong></span>
          <span className="sbm-count reserved">Rezerve: <strong>{reservedSeats.size}</strong></span>
        </div>
        <div className="sbm-actions">
          {Object.keys(blockedSeats).length > 0 && (
            <button className="sbm-clear-btn" onClick={handleClearAll}>🗑️ Temizle</button>
          )}
          <button className="sbm-save-btn" onClick={handleSave} disabled={saving || !hasChanges}>
            {saving ? '⏳ Kaydediliyor...' : '💾 Kaydet'}
          </button>
        </div>
      </div>

      {/* Lejant */}
      <div className="sbm-legend">
        <div className="sbm-legend-item"><span className="sbm-legend-dot available" /> Boş</div>
        <div className="sbm-legend-item"><span className="sbm-legend-dot protocol" /> Protokol</div>
        <div className="sbm-legend-item"><span className="sbm-legend-dot guest" /> Davetli</div>
        <div className="sbm-legend-item"><span className="sbm-legend-dot reserved" /> Rezerve</div>
      </div>

      {/* Sahne */}
      <div className="sbm-stage">🎬 SAHNE 🎬</div>

      {/* Salon planı */}
      <div className="sbm-map-scroll">
        {hasPositions && sectionPositions ? (
          // 2D konumlandırılmış layout
          <div className="sbm-map-2d" style={{ width: canvasW, height: canvasH }}>
            {venue.sections.map(section => {
              const pos = sectionPositions.get(section.id) || { left: 0, top: 0 };
              return renderSection(section, {
                position: 'absolute',
                left: pos.left,
                top: pos.top,
              });
            })}
          </div>
        ) : (
          // Liste layout (fallback)
          <div className="sbm-map">
            {[...venue.sections]
              .sort((a, b) => (a.position?.y || 999) - (b.position?.y || 999))
              .map(section => renderSection(section))}
          </div>
        )}
      </div>

      {hasChanges && (
        <div className="sbm-unsaved-bar">
          ⚠️ Kaydedilmemiş değişiklikler var
          <button className="sbm-save-btn-inline" onClick={handleSave} disabled={saving}>
            {saving ? 'Kaydediliyor...' : '💾 Kaydet'}
          </button>
        </div>
      )}
    </div>
  );
};

export default SeatBlockManager;